// This optional code is used to register a service worker.
// register() is not called by default.

import Logger from 'services/Logger'

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
)

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
}

export function register(config?: Config) {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    Logger.red('Service workers not supported in this browser')
    return
  }

  // Check for private browsing mode (iOS Safari blocks SW in private mode)
  try {
    // This will throw in iOS private browsing
    if ('storage' in navigator && navigator.storage) {
      navigator.storage.estimate().catch(() => {
        Logger.red('Service workers may be blocked (private browsing?)')
      })
    }
  } catch (e) {
    Logger.red('Storage API check failed:', e)
  }

  // The URL constructor is available in all browsers that support SW.
  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
  if (publicUrl.origin !== window.location.origin) {
    // Our service worker won't work if PUBLIC_URL is on a different origin
    // from what our page is served on. This might happen if a CDN is used to
    // serve assets; see https://github.com/facebook/create-react-app/issues/2374
    Logger.red('Service worker origin mismatch')
    return
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`

    if (isLocalhost) {
      // This is running on localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, config)

      // Add some additional logging to localhost, pointing developers to the
      // service worker/PWA documentation.
      navigator.serviceWorker.ready.then(() => {
        console.log(
          'This web app is being served cache-first by a service ' +
            'worker. To learn more, visit https://cra.link/PWA'
        )
      })
    } else {
      // Is not localhost. Just register service worker
      registerValidSW(swUrl, config)
    }
  })
}

function registerValidSW(swUrl: string, config?: Config) {
  // First, verify the service worker file is accessible
  fetch(swUrl, {
    method: 'HEAD',
    cache: 'no-cache'
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Service worker file returned ${response.status}`)
      }

      // Check MIME type
      const contentType = response.headers.get('content-type')
      if (contentType && !contentType.includes('javascript')) {
        throw new Error(`Invalid content-type: ${contentType}`)
      }

      // File is accessible, proceed with registration
      return navigator.serviceWorker.register(swUrl, {
        // Update on navigation (more reliable on mobile)
        updateViaCache: 'none'
      })
    })
    .then((registration) => {
      // Check for updates when page becomes visible (mobile-friendly)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          // On mobile, check if online before attempting update
          if (isMobile && !navigator.onLine) {
            Logger.blue('Skipping SW update check - device is offline')
            return
          }

          Logger.blue('Page visible - checking for SW updates')
          registration.update().catch((err) => {
            // Don't log to Sentry - this is expected on mobile
            Logger.red('SW update check failed:', err.message)
          })
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Periodic checks only on desktop (not mobile-friendly)
      // Use Page Visibility API instead for better mobile support
      let updateInterval: NodeJS.Timeout | null = null

      // Check if we're on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

      if (!isMobile) {
        // Only set up periodic checks on desktop
        updateInterval = setInterval(() => {
          if (document.visibilityState === 'visible') {
            registration.update().catch((err) => {
              Logger.red('SW update check failed:', err.message)
            })
          }
        }, 60 * 1000)
      }

      // Clean up on page unload
      window.addEventListener('beforeunload', () => {
        if (updateInterval) {
          clearInterval(updateInterval)
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      })

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          Logger.blue('Service worker updated:', event.data.message)
          // Optionally reload the page to use new version
          // window.location.reload()
        }
      })

      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (installingWorker == null) {
          return
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // New service worker is waiting, tell it to skip waiting
              Logger.orange(
                'New service worker available - activating immediately'
              )
              installingWorker.postMessage({ type: 'SKIP_WAITING' })

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration)
              }

              // Optionally auto-reload after a short delay
              setTimeout(() => {
                Logger.blue('Reloading to apply service worker update')
                window.location.reload()
              }, 1000)
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.')

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration)
              }
            }
          }
        }
      }
    })
    .catch((error) => {
      // Better error handling - don't throw on mobile if SW not supported
      const errorMessage = error.message || error.toString()

      // Don't log to Sentry for expected failures on mobile
      if (error.name === 'SecurityError') {
        Logger.red(
          'Service workers blocked by security policy (private browsing?)'
        )
      } else if (error.name === 'NotSupportedError') {
        Logger.red('Service workers not supported in this browser/context')
      } else if (
        errorMessage.includes('load failed') ||
        errorMessage.includes('404')
      ) {
        Logger.red(
          'Service worker file not found or failed to load:',
          errorMessage
        )
      } else if (errorMessage.includes('Invalid content-type')) {
        Logger.red('Service worker MIME type error:', errorMessage)
      } else {
        // Only log unexpected errors
        console.error('Unexpected service worker error:', error)
      }
    })
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
    cache: 'no-cache' // Important for mobile - don't use cached version
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type')
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        Logger.red('Service worker not found or invalid content-type')
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload()
          })
        })
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config)
      }
    })
    .catch((error) => {
      Logger.red(
        'Service worker check failed (offline or network error):',
        error.message
      )
      // Don't try to register if we can't even fetch the file
      // This is expected when offline, so don't show as error to user
    })
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister()
      })
      .catch((error) => {
        console.error(error.message)
      })
  }
}

/**
 * Force check for service worker updates
 * Can be called manually from anywhere in the app
 */
export function checkForUpdates() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        Logger.blue('Manually checking for service worker updates...')
        return registration.update()
      })
      .then(() => {
        Logger.blue('Update check complete')
      })
      .catch((error) => {
        Logger.red('Update check failed:', error)
      })
  } else {
    Logger.red('Service workers not supported')
  }
}

/**
 * Get current service worker registration
 */
export function getRegistration(): Promise<
  ServiceWorkerRegistration | undefined
> {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
  }
  return Promise.resolve(undefined)
}

// Expose update checker to window for debugging
if (process.env.NODE_ENV === 'development') {
  ;(window as any).checkSWUpdate = checkForUpdates
  ;(window as any).getSWRegistration = getRegistration
}
